import { useEffect, useState } from "react";
import {
  collection, doc, addDoc, updateDoc, onSnapshot, query, where, serverTimestamp, arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { S, C } from "../ui";
import SignaturePad from "../core/SignaturePad";
import {
  uploadWorkOrderSignature, buildWorkOrderPdf, sendWorkOrderEmail, sendWorkOrderText,
} from "../lib/workOrders";

// Ported from CustodialView's three-stage Work Orders flow (Admin creates
// unassigned -> Contractor assigns own crew -> crew member has the client
// sign on-site), adapted for this app's real-membership model and its
// two assignee shapes (vendor vs internal staff — see firestore.rules'
// "Work Orders" comment). All three role-facing tabs live in this one
// file, same convention as WorkersPanel.jsx/EntityDetailPanel.jsx
// sharing one file across roles.

function statusLabel(wo) {
  const crew = wo.assignedCrew || [];
  const sigs = wo.signatures || [];
  if (crew.length === 0) return { text: "Awaiting vendor assignment", tone: C.muted };
  if (sigs.length >= crew.length) return { text: "Fully signed", tone: C.mintDeep };
  return { text: `${sigs.length}/${crew.length} signed`, tone: C.accent };
}

function fmtDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ── Shared: Download / Email / Text a work order's PDF ──────────────────
// Used by the Facility Admin and Vendor Admin views (mirrors
// CustodialView's WorkOrderActions placement — the office side shares the
// paperwork out, not the on-site signer). Email goes through SendGrid
// (see functions/index.js's sendWorkOrderEmail) via the sending domain
// mail.edificeview.com, authenticated 2026-09-29. Text still depends on
// this app's Twilio A2P Campaign being approved (in review as of this
// build) — Text will fail with a clear error until that clears, same
// "code complete, blocked on real credentials" shape as elsewhere in
// this app. Download always works (client-side only, no backend).
function WorkOrderActions({ wo, facilityName, facilityId }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [recipient, setRecipient] = useState("");
  const [showRecipient, setShowRecipient] = useState(null); // "email" | "text" | null

  const download = async () => {
    setBusy("download"); setMsg("");
    try {
      const pdf = await buildWorkOrderPdf(wo, facilityName);
      pdf.save(`${wo.title.replace(/[^\w-]+/g, "_")}.pdf`);
    } catch (e) {
      setMsg("Couldn't build the PDF — try again.");
    }
    setBusy("");
  };

  const email = async () => {
    if (!recipient.trim()) return;
    setBusy("email"); setMsg("");
    try {
      const pdf = await buildWorkOrderPdf(wo, facilityName);
      const pdfBase64 = pdf.output("datauristring").split(",")[1];
      await sendWorkOrderEmail({
        facilityId, workOrderId: wo.id, to: recipient.trim(),
        pdfBase64, fileName: `${wo.title}.pdf`, subject: `Work order: ${wo.title}`,
      });
      setMsg(`✓ Emailed to ${recipient.trim()}`);
      setShowRecipient(null); setRecipient("");
    } catch (e) {
      setMsg(`✗ ${e.message}`);
    }
    setBusy("");
  };

  const text = async () => {
    if (!recipient.trim()) return;
    setBusy("text"); setMsg("");
    try {
      const pdf = await buildWorkOrderPdf(wo, facilityName);
      const blob = pdf.output("blob");
      const path = `workOrders/${wo.id}/shared_${Date.now()}.pdf`;
      const r = ref(storage, path);
      await uploadBytes(r, blob);
      const pdfUrl = await getDownloadURL(r);
      await sendWorkOrderText({ facilityId, to: recipient.trim(), pdfUrl, title: wo.title });
      setMsg(`✓ Texted to ${recipient.trim()}`);
      setShowRecipient(null); setRecipient("");
    } catch (e) {
      setMsg(`✗ ${e.message}`);
    }
    setBusy("");
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px" }} disabled={!!busy} onClick={download}>
          {busy === "download" ? "Building…" : "Download"}
        </button>
        <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px" }} disabled={!!busy}
          onClick={() => setShowRecipient(showRecipient === "email" ? null : "email")}>Email</button>
        <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px" }} disabled={!!busy}
          onClick={() => setShowRecipient(showRecipient === "text" ? null : "text")}>Text</button>
      </div>
      {showRecipient && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input style={{ ...S.input, marginBottom: 0, maxWidth: 260 }}
            placeholder={showRecipient === "email" ? "Recipient email" : "Recipient phone (+1...)"}
            value={recipient} onChange={(e) => setRecipient(e.target.value)} autoFocus />
          <button style={{ ...S.btn(), width: "auto", padding: "8px 16px" }} disabled={!!busy}
            onClick={showRecipient === "email" ? email : text}>
            {busy === showRecipient ? "Sending…" : "Send"}
          </button>
        </div>
      )}
      {msg && <div style={{ fontSize: 12.5, marginTop: 6, color: msg.startsWith("✓") ? C.mintDeep : "#B45309" }}>{msg}</div>}
    </div>
  );
}

// ── Facility Admin ────────────────────────────────────────────────────
export function FacilityWorkOrdersTab({ facilityId, facilityName, vendors }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [internalStaff, setInternalStaff] = useState([]);
  const [creating, setCreating] = useState(false);
  const [assigneeType, setAssigneeType] = useState("vendor");
  const [vendorId, setVendorId] = useState("");
  const [staffIds, setStaffIds] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => onSnapshot(query(collection(db, "workOrders"), where("facilityId", "==", facilityId)), (s) =>
    setWorkOrders(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)))), [facilityId]);

  useEffect(() => onSnapshot(query(collection(db, "workers"), where("facilityId", "==", facilityId)), (s) =>
    setInternalStaff(s.docs.map((d) => ({ id: d.id, ...d.data() })).filter((w) => w.roleType === "internal_staff"))), [facilityId]);

  const create = async () => {
    if (!title.trim()) { setErr("Give it a title."); return; }
    if (assigneeType === "vendor" && !vendorId) { setErr("Pick a vendor."); return; }
    if (assigneeType === "internal_staff" && staffIds.length === 0) { setErr("Pick at least one staff member."); return; }
    setErr(""); setBusy(true);
    try {
      const vendor = assigneeType === "vendor" ? vendors.find((v) => v.id === vendorId) : null;
      await addDoc(collection(db, "workOrders"), {
        facilityId,
        assigneeType,
        vendorId: assigneeType === "vendor" ? vendorId : null,
        vendorName: vendor ? vendor.companyName : null,
        title: title.trim(),
        description: description.trim(),
        assignedCrew: assigneeType === "internal_staff"
          ? internalStaff.filter((w) => staffIds.includes(w.id)).map((w) => ({ id: w.id, name: w.name }))
          : [],
        signatures: [],
        createdAt: serverTimestamp(),
      });
      setTitle(""); setDescription(""); setVendorId(""); setStaffIds([]); setCreating(false);
    } catch (e) {
      setErr("Couldn't create the work order — try again.");
    }
    setBusy(false);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={S.kicker}>Work Orders</div>
        <button style={{ ...S.btn(), width: "auto", padding: "10px 18px" }} onClick={() => setCreating((c) => !c)}>
          {creating ? "Cancel" : "+ New Work Order"}
        </button>
      </div>

      {creating && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, padding: 20, marginBottom: 20, maxWidth: 480 }}>
          {err && <div style={S.err}>{err}</div>}
          <input style={S.input} placeholder="Title (e.g. Q3 HVAC filter replacement)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea style={{ ...S.input, minHeight: 70, fontFamily: "inherit" }} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />

          <div style={{ ...S.kicker, margin: "12px 0 8px" }}>Assign to</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button type="button" onClick={() => setAssigneeType("vendor")}
              style={{ ...S.pill(assigneeType === "vendor" ? C.ink : C.white, assigneeType === "vendor" ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer" }}>A Vendor</button>
            <button type="button" onClick={() => setAssigneeType("internal_staff")}
              style={{ ...S.pill(assigneeType === "internal_staff" ? C.ink : C.white, assigneeType === "internal_staff" ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer" }}>Internal Staff</button>
          </div>

          {assigneeType === "vendor" ? (
            <select style={{ ...S.input, background: C.white }} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">Select a vendor…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.companyName} — {v.trade}</option>)}
            </select>
          ) : (
            <div style={{ border: `1px solid ${C.border}`, padding: "8px 10px", marginBottom: 10, maxHeight: 160, overflowY: "auto" }}>
              {internalStaff.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No internal staff on this building yet.</div>}
              {internalStaff.map((w) => (
                <label key={w.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "5px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={staffIds.includes(w.id)}
                    onChange={(e) => setStaffIds((ids) => e.target.checked ? [...ids, w.id] : ids.filter((id) => id !== w.id))} />
                  {w.name} <span style={{ color: C.muted }}>· {w.trade}</span>
                </label>
              ))}
            </div>
          )}

          <button style={{ ...S.btn(), marginTop: 6 }} disabled={busy} onClick={create}>{busy ? "Creating…" : "Create Work Order"}</button>
        </div>
      )}

      {workOrders.length === 0 && !creating && <div style={{ color: C.muted, fontSize: 13 }}>No work orders yet.</div>}
      <div style={{ display: "grid", gap: 12 }}>
        {workOrders.map((wo) => {
          const status = statusLabel(wo);
          return (
            <div key={wo.id} style={{ background: C.white, border: `1px solid ${C.border}`, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{wo.title}</div>
                  <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
                    {wo.assigneeType === "vendor" ? (wo.vendorName || "Vendor") : "Internal Staff"} · {fmtDate(wo.createdAt)}
                  </div>
                </div>
                <span style={{ ...S.pill(C.bg, status.tone), border: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{status.text}</span>
              </div>
              {wo.description && <div style={{ fontSize: 13, marginTop: 8, color: "#374151" }}>{wo.description}</div>}
              <WorkOrderActions wo={wo} facilityName={facilityName} facilityId={facilityId} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Vendor Admin ──────────────────────────────────────────────────────
export function VendorWorkOrdersTab({ facilityId, facilityName, vendorId, vendorName }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [crew, setCrew] = useState([]);
  const [assigning, setAssigning] = useState(null); // work order id currently assigning
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => onSnapshot(query(collection(db, "workOrders"), where("vendorId", "==", vendorId)), (s) =>
    setWorkOrders(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)))), [vendorId]);

  useEffect(() => onSnapshot(query(collection(db, "workers"), where("vendorId", "==", vendorId)), (s) =>
    setCrew(s.docs.map((d) => ({ id: d.id, ...d.data() })))), [vendorId]);

  const startAssign = (wo) => { setAssigning(wo.id); setPicked([]); };

  const confirmAssign = async (wo) => {
    if (picked.length === 0) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "workOrders", wo.id), {
        assignedCrew: crew.filter((c) => picked.includes(c.id)).map((c) => ({ id: c.id, name: c.name })),
      });
      setAssigning(null); setPicked([]);
    } catch (e) { /* surfaced via still-showing assign UI */ }
    setBusy(false);
  };

  return (
    <div>
      <div style={{ ...S.kicker, marginBottom: 16 }}>Work Orders</div>
      {workOrders.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No work orders yet.</div>}
      <div style={{ display: "grid", gap: 12 }}>
        {workOrders.map((wo) => {
          const status = statusLabel(wo);
          const unassigned = (wo.assignedCrew || []).length === 0;
          return (
            <div key={wo.id} style={{ background: C.white, border: `1px solid ${C.border}`, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{wo.title}</div>
                  <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>{fmtDate(wo.createdAt)}</div>
                </div>
                <span style={{ ...S.pill(C.bg, status.tone), border: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{status.text}</span>
              </div>
              {wo.description && <div style={{ fontSize: 13, marginTop: 8, color: "#374151" }}>{wo.description}</div>}

              {unassigned && (
                assigning === wo.id ? (
                  <div style={{ marginTop: 10, border: `1px solid ${C.border}`, padding: 10 }}>
                    {crew.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>Add a crew member first, under "Your Crew."</div>}
                    {crew.map((c) => (
                      <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "5px 0", cursor: "pointer" }}>
                        <input type="checkbox" checked={picked.includes(c.id)}
                          onChange={(e) => setPicked((ids) => e.target.checked ? [...ids, c.id] : ids.filter((id) => id !== c.id))} />
                        {c.name}
                      </label>
                    ))}
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px" }} onClick={() => setAssigning(null)}>Cancel</button>
                      <button style={{ ...S.btn(), width: "auto", padding: "6px 14px" }} disabled={busy || picked.length === 0} onClick={() => confirmAssign(wo)}>
                        {busy ? "Assigning…" : "Confirm"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px", marginTop: 10 }} onClick={() => startAssign(wo)}>Assign to crew</button>
                )
              )}

              {!unassigned && <WorkOrderActions wo={wo} facilityName={facilityName} facilityId={facilityId} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Staff (internal or vendor crew) — sign a work order on-site ────────
function SignBlock({ wo, worker }) {
  const [siteContactName, setSiteContactName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const alreadySigned = (wo.signatures || []).some((s) => s.crewId === worker.id);

  const onSave = async (blob) => {
    if (!siteContactName.trim()) return;
    setBusy(true); setErr("");
    try {
      const signatureUrl = await uploadWorkOrderSignature(wo.id, worker.id, blob);
      await updateDoc(doc(db, "workOrders", wo.id), {
        signatures: arrayUnion({
          crewId: worker.id,
          crewName: worker.name,
          siteContactName: siteContactName.trim(),
          signatureUrl,
          signedAt: Date.now(),
          signedAtLabel: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        }),
      });
    } catch (e) {
      setErr("Couldn't save the signature — try again.");
    }
    setBusy(false);
  };

  if (alreadySigned) return <div style={{ fontSize: 13, color: C.mintDeep, marginTop: 8 }}>✓ Signed</div>;

  return (
    <div style={{ marginTop: 10 }}>
      {err && <div style={S.err}>{err}</div>}
      <input style={{ ...S.input, maxWidth: 300 }} placeholder="Site contact's name" value={siteContactName} onChange={(e) => setSiteContactName(e.target.value)} disabled={busy} />
      {siteContactName.trim() && (
        busy ? <div style={{ fontSize: 13, color: C.muted }}>Saving…</div> : <SignaturePad onSave={onSave} />
      )}
    </div>
  );
}

export function StaffWorkOrdersTab({ worker }) {
  const [workOrders, setWorkOrders] = useState([]);

  const listField = worker.vendorId ? "vendorId" : "facilityId";
  const listValue = worker.vendorId || worker.facilityId;

  useEffect(() => onSnapshot(query(collection(db, "workOrders"), where(listField, "==", listValue)), (s) =>
    setWorkOrders(s.docs.map((d) => ({ id: d.id, ...d.data() }))
      .filter((wo) => (wo.assignedCrew || []).some((c) => c.id === worker.id))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)))), [listField, listValue, worker.id]);

  if (workOrders.length === 0) return <div style={{ color: C.muted, fontSize: 13 }}>No work orders assigned to you.</div>;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {workOrders.map((wo) => {
        const status = statusLabel(wo);
        return (
          <div key={wo.id} style={{ background: C.white, border: `1px solid ${C.border}`, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{wo.title}</div>
              <span style={{ ...S.pill(C.bg, status.tone), border: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{status.text}</span>
            </div>
            {wo.description && <div style={{ fontSize: 13, marginTop: 8, color: "#374151" }}>{wo.description}</div>}
            <SignBlock wo={wo} worker={worker} />
          </div>
        );
      })}
    </div>
  );
}
